import React, { useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';

import { HangingProtocolService, CommandsManager } from '@ohif/core';
import { useAppConfig } from '@state';
import ViewerHeader from './ViewerHeader';
import SidePanelWithServices from '../Components/SidePanelWithServices';
import { Onboarding, ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@ohif/ui-next';
import useResizablePanels from './ResizablePanelsHook';
import { isPatientMode } from '../../../../platform/app/src/custom/utils/patientMode';
import { resetI18nToStaffDefault } from '../../../../platform/app/src/custom/utils/patientViewLanguage';

import './viewerRevamp.css';

/** Hairline separator; `w-px` overrides default `w-1` on ResizableHandle (tailwind-merge). */
const resizableHandleClassName =
  'w-px max-w-px shrink-0 bg-viewer-border hover:bg-viewer-accent/25 [&[data-resize-handle-state=drag]]:bg-viewer-accent/40';

/** Below this width the series rail would crush the image viewport — start collapsed. */
const NARROW_VIEWPORT_MQ = '(max-width: 767px)';

const isNarrowViewport = () =>
  typeof window !== 'undefined' && window.matchMedia(NARROW_VIEWPORT_MQ).matches;

/** Nudge Cornerstone/WebGL after layout changes without unmounting canvases. */
const dispatchViewportResize = () => {
  if (typeof window === 'undefined') {
    return;
  }
  window.requestAnimationFrame(() => {
    window.dispatchEvent(new Event('resize'));
  });
};

function ViewerLayout({
  // From Extension Module Params
  extensionManager,
  servicesManager,
  hotkeysManager,
  commandsManager,
  // From Modes
  viewports,
  ViewportGridComp,
  leftPanelClosed = false,
  rightPanelClosed = false,
  leftPanelResizable = false,
  rightPanelResizable = false,
  leftPanelInitialExpandedWidth,
  rightPanelInitialExpandedWidth,
  leftPanelMinimumExpandedWidth,
  rightPanelMinimumExpandedWidth,
}: withAppTypes): React.FunctionComponent {
  const [appConfig] = useAppConfig();
  const patientMode = isPatientMode();

  // Staff viewer must not inherit Hindi from a prior patient session in the same browser
  useEffect(() => {
    if (!patientMode) {
      resetI18nToStaffDefault();
    }
  }, [patientMode]);

  const { panelService, hangingProtocolService, customizationService } = servicesManager.services;
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(appConfig.showLoadingIndicator);

  const hasPanels = useCallback(
    (side): boolean => !!panelService.getPanels(side).length,
    [panelService]
  );

  const [hasRightPanels, setHasRightPanels] = useState(hasPanels('right'));
  const [hasLeftPanels, setHasLeftPanels] = useState(hasPanels('left'));
  // Collapse series rail on first paint for narrow patient screens; later opens must be allowed to stick.
  const [leftPanelClosedState, setLeftPanelClosed] = useState(
    () => leftPanelClosed || isNarrowViewport()
  );
  const [rightPanelClosedState, setRightPanelClosed] = useState(rightPanelClosed);

  const [
    leftPanelProps,
    rightPanelProps,
    resizablePanelGroupProps,
    resizableLeftPanelProps,
    resizableViewportGridPanelProps,
    resizableRightPanelProps,
    onHandleDragging,
  ] = useResizablePanels(
    leftPanelClosedState,
    setLeftPanelClosed,
    rightPanelClosedState,
    setRightPanelClosed,
    hasLeftPanels,
    hasRightPanels,
    leftPanelInitialExpandedWidth,
    rightPanelInitialExpandedWidth,
    leftPanelMinimumExpandedWidth,
    rightPanelMinimumExpandedWidth
  );

  // Stable refs so the narrow-MQ listener never re-subscribes / never re-closes an open panel.
  const onLeftPanelCloseRef = useRef(leftPanelProps.onClose);
  onLeftPanelCloseRef.current = leftPanelProps.onClose;

  const handleMouseEnter = () => {
    (document.activeElement as HTMLElement)?.blur();
  };

  const LoadingIndicatorProgress = customizationService.getCustomization(
    'ui.loadingIndicatorProgress'
  );

  useEffect(() => {
    document.body.classList.remove('bg-black');
    document.body.classList.add('bg-viewer-canvas', 'overflow-hidden');

    return () => {
      document.body.classList.remove('bg-viewer-canvas', 'overflow-hidden', 'bg-black');
    };
  }, []);

  /** Collapse series rail when crossing into a narrow viewport (not while already narrow). */
  useEffect(() => {
    const mediaQuery = window.matchMedia(NARROW_VIEWPORT_MQ);
    const onChange = (event: MediaQueryListEvent) => {
      if (event.matches) {
        onLeftPanelCloseRef.current();
      }
    };

    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    dispatchViewportResize();
  }, [leftPanelClosedState, rightPanelClosedState]);

  const getComponent = id => {
    const entry = extensionManager.getModuleEntry(id);

    if (!entry || !entry.component) {
      throw new Error(
        `${id} is not valid for an extension module or no component found from extension ${id}. Please verify your configuration or ensure that the extension is properly registered. It's also possible that your mode is utilizing a module from an extension that hasn't been included in its dependencies (add the extension to the "extensionDependencies" array in your mode's index.js file). Check the reference string to the extension in your Mode configuration`
      );
    }

    return { entry };
  };

  useEffect(() => {
    const { unsubscribe } = hangingProtocolService.subscribe(
      HangingProtocolService.EVENTS.PROTOCOL_CHANGED,
      () => {
        setShowLoadingIndicator(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [hangingProtocolService]);

  const getViewportComponentData = viewportComponent => {
    const { entry } = getComponent(viewportComponent.namespace);

    return {
      component: entry.component,
      isReferenceViewable: entry.isReferenceViewable,
      displaySetsToDisplay: viewportComponent.displaySetsToDisplay,
    };
  };

  useEffect(() => {
    const { unsubscribe } = panelService.subscribe(
      panelService.EVENTS.PANELS_CHANGED,
      ({ options }) => {
        setHasLeftPanels(hasPanels('left'));
        setHasRightPanels(hasPanels('right'));
        // Honor explicit open/close — do not OR with isNarrowViewport() (that blocked mobile opens).
        if (options?.leftPanelClosed !== undefined) {
          setLeftPanelClosed(options.leftPanelClosed);
        }
        if (options?.rightPanelClosed !== undefined) {
          setRightPanelClosed(options.rightPanelClosed);
        }
      }
    );

    return () => {
      unsubscribe();
    };
  }, [panelService, hasPanels]);

  const viewportComponents = viewports.map(getViewportComponentData);

  return (
    <div
      className={[
        'viewer-layout-revamp flex h-screen flex-col bg-viewer-canvas',
        patientMode ? 'viewer-patient-mode' : '',
        leftPanelClosedState ? 'viewer-layout--left-collapsed' : 'viewer-layout--left-expanded',
        rightPanelClosedState ? 'viewer-layout--right-collapsed' : 'viewer-layout--right-expanded',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <ViewerHeader
        hotkeysManager={hotkeysManager}
        extensionManager={extensionManager}
        servicesManager={servicesManager}
        appConfig={appConfig}
      />
      <div className="relative flex min-h-0 w-full flex-1 flex-row flex-nowrap items-stretch overflow-hidden bg-viewer-canvas">
        <React.Fragment>
          {showLoadingIndicator && <LoadingIndicatorProgress className="h-full w-full bg-black" />}
          <ResizablePanelGroup {...resizablePanelGroupProps}>
            {hasLeftPanels ? (
              <>
                <ResizablePanel {...resizableLeftPanelProps}>
                  <SidePanelWithServices
                    side="left"
                    isExpanded={!leftPanelClosedState}
                    servicesManager={servicesManager}
                    {...leftPanelProps}
                    panelVariant="light"
                  />
                </ResizablePanel>
                <ResizableHandle
                  onDragging={onHandleDragging}
                  disabled={!leftPanelResizable}
                  className={`${resizableHandleClassName} viewer-layout-left-resize-handle`}
                />
              </>
            ) : null}
            <ResizablePanel {...resizableViewportGridPanelProps}>
              <div className="viewer-viewport-mat bg-viewer-mat flex h-full min-h-0 w-full flex-col">
                <div
                  className="viewer-viewport-stage relative min-h-0 flex-1 overflow-hidden rounded-md bg-black shadow-sm ring-1 ring-neutral-400/25"
                  onMouseEnter={handleMouseEnter}
                >
                  <ViewportGridComp
                    servicesManager={servicesManager}
                    viewportComponents={viewportComponents}
                    commandsManager={commandsManager}
                  />
                </div>
              </div>
            </ResizablePanel>
            {hasRightPanels ? (
              <>
                <ResizableHandle
                  onDragging={onHandleDragging}
                  disabled={!rightPanelResizable}
                  className={`${resizableHandleClassName} viewer-layout-right-resize-handle`}
                />
                <ResizablePanel {...resizableRightPanelProps}>
                  <SidePanelWithServices
                    side="right"
                    isExpanded={!rightPanelClosedState}
                    servicesManager={servicesManager}
                    {...rightPanelProps}
                    panelVariant="light"
                  />
                </ResizablePanel>
              </>
            ) : null}
          </ResizablePanelGroup>
        </React.Fragment>
      </div>
      <Onboarding tours={customizationService.getCustomization('ohif.tours')} />
    </div>
  );
}

ViewerLayout.propTypes = {
  extensionManager: PropTypes.shape({
    getModuleEntry: PropTypes.func.isRequired,
  }).isRequired,
  commandsManager: PropTypes.instanceOf(CommandsManager),
  servicesManager: PropTypes.object.isRequired,
  leftPanels: PropTypes.array,
  rightPanels: PropTypes.array,
  leftPanelClosed: PropTypes.bool.isRequired,
  rightPanelClosed: PropTypes.bool.isRequired,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  viewports: PropTypes.array,
};

export default ViewerLayout;
